import { ChangeEvent } from 'react';
import { Box, styled } from '@mui/material';

import FormatNumberInput from '@Components/FormattedNumberInput';
import { FormInput } from '@Components/Form';
import { disabledFormInputStyles } from '@Components/Form/styles';
import {
  EXPORT_INCOME_TYPE_ID,
  OTHER_INCOME_STRUCTURE_ID,
  INCOME_SUBGROUP_CATEGORY,
  TOTAL_INCOME_STRUCTURE_ID,
} from 'src/Enum/IncomeStructure';
import {
  IIncomeItemFormValues,
  IIncomeStructureFormValues,
} from '../../NatureOfBusinessFormProvider/types';
import { useNatureOfBusinessForm } from '../../NatureOfBusinessFormProvider';
import {
  calcIncomeItemPct,
  calcNetIncomeItem,
  filterAndSortBySubgroup,
  sumExtendedValues,
  yearFields,
} from '../utils';

const FormNumberInput = styled(FormInput)({
  '& input': {
    textAlign: 'right',
  },
});

type ExportIncomeYearFieldsFormProps = {
  keyItem: string;
};

const ExportIncomeYearFieldsForm = ({
  keyItem,
}: ExportIncomeYearFieldsFormProps) => {
  const { watch, setValue } = useNatureOfBusinessForm();

  const incomeStructure = watch(
    'incomeStructure'
  ) as IIncomeStructureFormValues;

  const exportIncomeItems = incomeStructure?.exportIncomeItems || {};
  const exportIncomePercentages =
    incomeStructure?.exportIncomePercentages || {};
  const { isCompletedTh = false, isCompletedEn = false } =
    incomeStructure?.completedSection ?? {};
  const exportIncomeExtended = filterAndSortBySubgroup(
    exportIncomeItems,
    INCOME_SUBGROUP_CATEGORY.EXPORT_INCOME
  );

  const handleChange = (
    event: ChangeEvent<HTMLInputElement>,
    yearField: keyof IIncomeItemFormValues
  ) => {
    const value = event.target.value;
    const totalValue = exportIncomeItems[
      TOTAL_INCOME_STRUCTURE_ID.TOTAL_EXPORT_INCOME
    ][yearField] as string;
    const internationalValue = exportIncomeItems[
      EXPORT_INCOME_TYPE_ID.INTERNATIONAL
    ][yearField] as string;

    if (
      keyItem === TOTAL_INCOME_STRUCTURE_ID.TOTAL_EXPORT_INCOME.toString() ||
      keyItem === EXPORT_INCOME_TYPE_ID.DOMESTIC.toString()
    ) {
      calcInternationalIncome(totalValue, yearField);
    }

    if (
      keyItem.startsWith(INCOME_SUBGROUP_CATEGORY.EXPORT_INCOME) &&
      internationalValue
    ) {
      calcExportIncomeOther(internationalValue, yearField);
    }

    if (
      keyItem === EXPORT_INCOME_TYPE_ID.INTERNATIONAL.toString() &&
      exportIncomeExtended.length !== 0
    ) {
      calcExportIncomeOther(internationalValue, yearField);
    }

    calcExportIncomePct(value, totalValue, internationalValue, yearField);
  };

  const calcInternationalIncome = (
    totalValue: string,
    yearField: keyof IIncomeItemFormValues
  ) => {
    const domesticValue = exportIncomeItems[EXPORT_INCOME_TYPE_ID.DOMESTIC][
      yearField
    ] as string;

    if (totalValue && domesticValue) {
      setValue(
        `incomeStructure.exportIncomeItems.${EXPORT_INCOME_TYPE_ID.INTERNATIONAL}.${yearField}`,
        calcNetIncomeItem(totalValue, domesticValue)
      );
    } else {
      setValue(
        `incomeStructure.exportIncomeItems.${EXPORT_INCOME_TYPE_ID.INTERNATIONAL}.${yearField}`,
        ''
      );
    }
  };

  const calcExportIncomeOther = (
    internationalValue: string,
    yearField: keyof IIncomeItemFormValues
  ) => {
    const extendedValues = sumExtendedValues(
      exportIncomeItems,
      INCOME_SUBGROUP_CATEGORY.EXPORT_INCOME,
      yearField
    );

    if (internationalValue !== '' && extendedValues !== '') {
      setValue(
        `incomeStructure.exportIncomeItems.${OTHER_INCOME_STRUCTURE_ID.OTHER_EXPORT_INCOME}.${yearField}`,
        calcNetIncomeItem(internationalValue, extendedValues)
      );
    } else {
      setValue(
        `incomeStructure.exportIncomeItems.${OTHER_INCOME_STRUCTURE_ID.OTHER_EXPORT_INCOME}.${yearField}`,
        ''
      );
    }
  };

  const calcExportIncomePct = (
    value: string,
    totalValue: string,
    internationalValue: string,
    yearField: keyof IIncomeItemFormValues
  ) => {
    if (!totalValue) {
      Object.keys(exportIncomePercentages).forEach((key) =>
        setValue(
          `incomeStructure.exportIncomePercentages.${key}.${yearField}`,
          ''
        )
      );
      return;
    }

    if (!internationalValue) {
      Object.keys(exportIncomePercentages)
        .filter((key) => key.startsWith(INCOME_SUBGROUP_CATEGORY.EXPORT_INCOME))
        .forEach((key) =>
          setValue(
            `incomeStructure.exportIncomePercentages.${key}.${yearField}`,
            ''
          )
        );
    }

    if (!value) {
      setValue(
        `incomeStructure.exportIncomePercentages.${keyItem}.${yearField}`,
        ''
      );
      return;
    }

    if (keyItem === OTHER_INCOME_STRUCTURE_ID.OTHER_EXPORT_INCOME.toString()) {
      setValue(
        `incomeStructure.exportIncomePercentages.${keyItem}.${yearField}`,
        calcIncomeItemPct(value, internationalValue)
      );
      return;
    }

    Object.entries(exportIncomeItems).forEach(([key, item]) => {
      const itemValue = item[yearField] as string;

      if (itemValue) {
        if (
          key.startsWith(INCOME_SUBGROUP_CATEGORY.EXPORT_INCOME) ||
          key === OTHER_INCOME_STRUCTURE_ID.OTHER_EXPORT_INCOME.toString()
        ) {
          setValue(
            `incomeStructure.exportIncomePercentages.${key}.${yearField}`,
            calcIncomeItemPct(itemValue, internationalValue)
          );
        } else {
          setValue(
            `incomeStructure.exportIncomePercentages.${key}.${yearField}`,
            calcIncomeItemPct(itemValue, totalValue)
          );
        }
      }
    });
  };

  return (
    <Box
      width={1}
      display={'grid'}
      gridTemplateColumns={'repeat(3, 1fr)'}
      gap={3}
      ml={1}
    >
      {yearFields.map((yearField) => (
        <FormNumberInput
          key={`${keyItem}-${yearField}`}
          name={`incomeStructure.exportIncomeItems.${keyItem}.${yearField}`}
          placeholder={'กรุณาระบุจำนวน'}
          slotProps={{
            input: {
              inputComponent: FormatNumberInput as React.ComponentType,
            },
          }}
          fullWidth
          sx={
            isCompletedTh ||
            isCompletedEn ||
            keyItem === TOTAL_INCOME_STRUCTURE_ID.TOTAL_EXPORT_INCOME.toString()
              ? disabledFormInputStyles
              : undefined
          }
          onChange={(event) =>
            handleChange(event as ChangeEvent<HTMLInputElement>, yearField)
          }
        />
      ))}
    </Box>
  );
};

export default ExportIncomeYearFieldsForm;
